import { useState, type JSX } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { KitOutProtocolWithItemsDto } from '@ctms/contracts';
import { ChevronDown, ChevronRight, Printer, ScrollText, X } from 'lucide-react';

import { kitsApi } from '../kits.api.js';
import { kitQueryKeys } from '../kits.query-keys.js';

export interface DestructionProtocolsDialogProps {
  trialId: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * documents:read — when wired, each register row gains the A4 print action
   * (issue #31 B1, REQ-DOC-018). RBAC by omission: the cockpit passes the
   * callback only with the permission, exactly like `onPrintVcl` (VCL pattern).
   */
  onPrint?: (protocol: KitOutProtocolWithItemsDto, labName: string) => void;
}

const PAGE_SIZE = 10;

/**
 * Register of destruction protocols (REQ-KIT-032). Read-only — protocols are
 * created from the virtual basket. The list endpoint already returns items;
 * expanding a row refetches the detail for freshness.
 */
export function DestructionProtocolsDialog({
  trialId,
  open,
  onOpenChange,
  onPrint,
}: DestructionProtocolsDialogProps): JSX.Element | null {
  const [labFilter, setLabFilter] = useState<number | ''>('');
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const laboratoriesQuery = useQuery({
    queryKey: kitQueryKeys.laboratories.byTrial(trialId),
    queryFn: () => kitsApi.listLaboratories({ trialId, page: 1, limit: 100 }),
    enabled: open && trialId > 0,
    staleTime: 5 * 60 * 1000,
  });
  const laboratories = laboratoriesQuery.data?.items ?? [];
  const labNameById = new Map(laboratories.map((lab) => [lab.id, lab.name]));

  const listQueryParams = {
    page,
    limit: PAGE_SIZE,
    trialLaboratoryId: labFilter === '' ? undefined : labFilter,
  };

  const listQuery = useQuery({
    queryKey: kitQueryKeys.protocols.list(listQueryParams),
    queryFn: () => kitsApi.listProtocols(listQueryParams),
    enabled: open,
    staleTime: 60 * 1000,
  });
  const protocols = listQuery.data?.items ?? [];
  const total = listQuery.data?.total ?? 0;
  const hasNext = listQuery.data?.hasNext ?? false;

  const detailQuery = useQuery({
    queryKey: kitQueryKeys.protocols.detail(expandedId ?? 0),
    queryFn: () => kitsApi.getProtocol(expandedId as number),
    enabled: expandedId !== null,
    staleTime: 60 * 1000,
  });

  function toggleExpand(id: number): void {
    setExpandedId((prev) => (prev === id ? null : id));
  }

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="destruction-protocols-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 p-4 backdrop-blur-xs"
    >
      <div className="flex max-h-[90vh] w-full max-w-4xl flex-col rounded-xl border border-slate-700 bg-slate-900 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-800 px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-slate-800 text-rose-400 border border-slate-700">
              <ScrollText className="h-5 w-5" />
            </div>
            <div>
              <h2 id="destruction-protocols-title" className="text-lg font-semibold text-slate-100">
                Rejestr protokołów zniszczenia
              </h2>
              <p className="text-xs text-slate-400">
                Trwałe zniszczenia zestawów laboratoryjnych (REQ-KIT-032)
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-800 hover:text-slate-200"
            aria-label="Zamknij"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          <div className="flex items-center justify-between gap-3">
            <label className="block text-xs">
              <span className="text-slate-400">Laboratorium</span>
              <select
                aria-label="Filtr laboratorium"
                value={labFilter}
                onChange={(e) => {
                  setLabFilter(e.target.value === '' ? '' : Number(e.target.value));
                  setPage(1);
                }}
                className="mt-1 w-64 rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100"
              >
                <option value="">Wszystkie laboratoria</option>
                {laboratories.map((lab) => (
                  <option key={lab.id} value={lab.id}>
                    {lab.name}
                  </option>
                ))}
              </select>
            </label>
            <div className="flex items-center gap-2 text-xs text-slate-400">
              <span>
                {total === 0 ? 'Brak protokołów' : `${total} protokołów · strona ${page}`}
              </span>
              <button
                type="button"
                aria-label="Poprzednia strona"
                disabled={page <= 1}
                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                className="rounded-lg border border-slate-700 px-2.5 py-1.5 hover:bg-slate-800 disabled:opacity-40"
              >
                ←
              </button>
              <button
                type="button"
                aria-label="Następna strona"
                disabled={!hasNext}
                onClick={() => setPage((prev) => prev + 1)}
                className="rounded-lg border border-slate-700 px-2.5 py-1.5 hover:bg-slate-800 disabled:opacity-40"
              >
                →
              </button>
            </div>
          </div>

          {listQuery.isLoading ? (
            <div className="p-6 text-center text-sm text-slate-500">Ładowanie rejestru…</div>
          ) : protocols.length === 0 ? (
            <div className="p-6 text-center text-sm text-slate-500">
              Brak protokołów zniszczenia dla wybranych kryteriów.
            </div>
          ) : (
            <table className="w-full text-left text-xs text-slate-200" id="destruction-protocols-table">
              <thead className="border-b border-slate-800 bg-slate-950/60 text-[11px] uppercase text-slate-400">
                <tr>
                  <th className="w-10 px-3 py-2"></th>
                  <th className="px-3 py-2">Protokół</th>
                  <th className="px-3 py-2">Laboratorium</th>
                  <th className="px-3 py-2">Data zniszczenia</th>
                  <th className="px-3 py-2 text-right">Pozycje</th>
                  <th className="px-3 py-2 text-right">Sztuk łącznie</th>
                  <th className="px-3 py-2">Uwagi</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/70">
                {protocols.map((protocol) => {
                  const isExpanded = expandedId === protocol.id;
                  const detailItems =
                    isExpanded && detailQuery.data?.id === protocol.id
                      ? detailQuery.data.items
                      : protocol.items;
                  const totalPieces = protocol.items.reduce(
                    (sum, item) => sum + item.numberOfKits,
                    0,
                  );
                  return (
                    <tr key={protocol.id}>
                      <td colSpan={7} className="p-0">
                        <div className="flex items-center w-full px-3 py-2.5">
                          <div className="w-7 shrink-0">
                            <button
                              type="button"
                              aria-label={
                                isExpanded
                                  ? `Zwiń pozycje protokołu ${protocol.id}`
                                  : `Rozwiń pozycje protokołu ${protocol.id}`
                              }
                              onClick={() => toggleExpand(protocol.id)}
                              className="text-slate-400 hover:text-cyan-400"
                            >
                              {isExpanded ? (
                                <ChevronDown className="h-4 w-4" />
                              ) : (
                                <ChevronRight className="h-4 w-4" />
                              )}
                            </button>
                          </div>
                          <div className="flex-1 font-mono font-semibold text-slate-100">
                            #{protocol.id}
                          </div>
                          <div className="w-44 truncate">
                            {labNameById.get(protocol.trialLaboratoryId) ??
                              `Laboratorium #${protocol.trialLaboratoryId}`}
                          </div>
                          <div className="w-32">{protocol.protocolDate}</div>
                          <div className="w-20 text-right font-mono">
                            {protocol.items.length}
                          </div>
                          <div className="w-24 text-right font-mono">{totalPieces}</div>
                          <div
                            className="flex-1 truncate pl-3 text-slate-400"
                            title={protocol.comments ?? undefined}
                          >
                            {protocol.comments ?? '—'}
                          </div>
                          {onPrint !== undefined && (
                            <div className="shrink-0 pl-3">
                              <button
                                type="button"
                                aria-label={`Wydrukuj protokół ${protocol.id}`}
                                data-testid={`print-protocol-${protocol.id}`}
                                title="Wydruk protokołu zniszczenia (A4 PDF)"
                                onClick={() =>
                                  onPrint(
                                    protocol,
                                    labNameById.get(protocol.trialLaboratoryId) ??
                                      `Laboratorium #${protocol.trialLaboratoryId}`,
                                  )
                                }
                                className="rounded-md border border-slate-700 p-1.5 text-slate-400 transition-colors hover:bg-slate-800 hover:text-cyan-300"
                              >
                                <Printer className="h-4 w-4" />
                              </button>
                            </div>
                          )}
                        </div>
                        {isExpanded && (
                          <div className="border-t border-slate-800 bg-slate-950/70 px-10 py-3">
                            {detailItems.length === 0 ? (
                              <div className="text-center text-xs text-slate-500">
                                Protokół nie zawiera pozycji.
                              </div>
                            ) : (
                              <table className="w-full text-left text-xs">
                                <thead className="border-b border-slate-800 text-[11px] uppercase text-slate-400">
                                  <tr>
                                    <th className="px-3 py-1.5">Zestaw</th>
                                    <th className="px-3 py-1.5">Partia</th>
                                    <th className="px-3 py-1.5 text-right">Sztuk</th>
                                    <th className="px-3 py-1.5">Data zdjęcia ze stanu</th>
                                    <th className="px-3 py-1.5">Uwagi pozycji</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-800/60 font-mono">
                                  {detailItems.map((item) => (
                                    <tr key={item.id} className="text-slate-300">
                                      <td className="px-3 py-1.5">Zestaw #{item.labKitId}</td>
                                      <td className="px-3 py-1.5">
                                        {item.labKitExpirationId === null
                                          ? 'bez partii'
                                          : `#${item.labKitExpirationId}`}
                                      </td>
                                      <td className="px-3 py-1.5 text-right">
                                        {item.numberOfKits}
                                      </td>
                                      <td className="px-3 py-1.5">{item.outDate}</td>
                                      <td className="px-3 py-1.5 font-sans">
                                        {item.comments ?? '—'}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
